import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';

// Shared non-dismissable full-screen shell for the auth gate screens (splash,
// Entra sign-in, enrollment, biometric lock). Tokens only; covers everything.
//
// Portalled to <body>, like the toasts, so `--z-lock` ranks it against them in the
// same stacking context: #root is a stacking context of its own, and an overlay
// inside it would paint under any toast however high its z-index. (b-02055)
export function AuthOverlay({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  const reduce = useReducedMotion();
  return createPortal(
    <div className="auth-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <motion.div
        className="auth-overlay__card"
        initial={reduce ? false : { opacity: 0, scale: 0.98, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      >
        <img src="/logo.svg" alt="" width={40} height={40} style={{ opacity: 0.9 }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div className="auth-overlay__badge">{icon}</div>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            {title}
          </h1>
          {subtitle && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0, maxWidth: 300 }}>
              {subtitle}
            </p>
          )}
        </div>
        {children && <div className="auth-overlay__actions">{children}</div>}
      </motion.div>
    </div>,
    document.body,
  );
}
